import { Injectable, inject, InjectionToken } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { EMPTY, Observable, Subject, from, of, defer } from 'rxjs';
import { catchError, concatMap, finalize, switchMap, tap } from 'rxjs/operators';
import { concatLatestFrom } from '@ngrx/operators';
import { MicroFavouriteAssets } from '../../types/micro-favourite-assets';
import { IAnalysisState, IState } from '@analysis/domain';
import { type AnalysisStateRepository } from '@analysis/use-cases';
import { AnalyzerEntity } from '@telespot/domain';
import Parse from 'parse';

export const ANALYSIS_STATE_SERVICE = new InjectionToken<AnalysisStateRepository>('ANALYSIS_STATE_SERVICE');

export type AnalysisState = Omit<IAnalysisState, 'bookmarkAssetId' | 'favouriteAssetIds' | 'name'>;

export interface AnalysisStateState {
  analysisStates: Map<string, AnalysisState>;
  favouriteAssets: Map<string, string[]>;
  bookmarkedAssets: Map<string, string>;
  loading: boolean;
  error: string | null;
  updatingCount: number;
}

@Injectable()
export class AnalysisStateStore extends ComponentStore<AnalysisStateState> {
  private readonly analysisStateApi = inject<AnalysisStateRepository>(ANALYSIS_STATE_SERVICE);

  constructor() {
    super({
      analysisStates: new Map(),
      favouriteAssets: new Map(),
      bookmarkedAssets: new Map(),
      loading: false,
      error: null,
      updatingCount: 0,
    });
  }

  // Selectors
  public readonly analysisStates$ = this.select((state) => state.analysisStates);
  public readonly favouriteAssets$ = this.select((state) => state.favouriteAssets);
  public readonly bookmarkedAssets$ = this.select((state) => state.bookmarkedAssets);
  public readonly loading$ = this.select((state) => state.loading);
  public readonly error$ = this.select((state) => state.error);
  public readonly updating$ = this.select((state) => state.updatingCount > 0);

  public readonly analysisStatesLoading$ = this.select(
    this.analysisStates$,
    (analysisStates) => !analysisStates || analysisStates.size === 0
  );

  public readonly selectedAnalyst$ = this.select(() => null);

  public readonly currentUserAnalysisState$ = this.select(
    this.analysisStates$,
    this.favouriteAssets$,
    (analysisStates, favouriteAssets): IAnalysisState | null => {
      const currentUserId = Parse.User.current()?.id;
      if (!currentUserId) return null;
      const analysisState = analysisStates.get(currentUserId);
      if (!analysisState) return null;
      return {
        ...analysisState,
        bookmarkAssetId: undefined,
        favouriteAssetIds: favouriteAssets.get(analysisState.id) || [],
      };
    }
  );

  readonly setAnalysisStates = this.updater((state, analysisStates: IAnalysisState[]) => {
    const newAnalysisStates = new Map<string, AnalysisState>();
    const newFavAssets = new Map<string, string[]>();
    const newBookmarks = new Map<string, string>();

    analysisStates.forEach((state) => {
      const { bookmarkAssetId, favouriteAssetIds, name, ...stateWithoutFavourites } = state;
      newAnalysisStates.set(state.analystId, stateWithoutFavourites);
      if (favouriteAssetIds && favouriteAssetIds.length > 0) {
        newFavAssets.set(state.id, favouriteAssetIds);
      }
      if (bookmarkAssetId) {
        newBookmarks.set(state.id, bookmarkAssetId);
      }
    });

    return {
      ...state,
      analysisStates: newAnalysisStates,
      favouriteAssets: newFavAssets,
      bookmarkedAssets: newBookmarks,
      error: null,
    };
  });

  /** Set (or clear, when assetId is null) the bookmarked asset for an analysis
   *  state. Kept separate from `updateAnalysisState` because that receives states
   *  whose volatile `bookmarkAssetId` has been stripped. */
  readonly setBookmarkForState = this.updater(
    (state, { historyId, assetId }: { historyId: string; assetId: string | null }) => {
      const bookmarkedAssets = new Map(state.bookmarkedAssets);
      if (assetId) {
        bookmarkedAssets.set(historyId, assetId);
      } else {
        bookmarkedAssets.delete(historyId);
      }
      return { ...state, bookmarkedAssets };
    }
  );

  readonly updateAnalysisState = this.updater((state, analysisState: IAnalysisState) => {
    const analysisStates = new Map(state.analysisStates);
    const { bookmarkAssetId, favouriteAssetIds, name, ...stateWithoutFavourites } = analysisState;
    analysisStates.set(analysisState.analystId, stateWithoutFavourites);

    return {
      ...state,
      analysisStates,
      error: null,
    };
  });

  readonly updateFavouriteAssets = this.updater((state, microFavouriteAssets: MicroFavouriteAssets) => {
    const favouriteAssets = new Map(state.favouriteAssets);
    favouriteAssets.set(microFavouriteAssets.historyId, microFavouriteAssets.favouriteAssets);

    return {
      ...state,
      favouriteAssets,
      error: null,
    };
  });

  readonly toggleFavouriteAsset = this.updater(
    (state, { historyId, assetId }: { historyId: string; assetId: string }) => {
      const favouriteAssets = new Map(state.favouriteAssets);
      const currentFavourites = favouriteAssets.get(historyId) || [];

      let updatedFavourites: string[];
      if (currentFavourites.includes(assetId)) {
        updatedFavourites = currentFavourites.filter((id) => id !== assetId);
      } else {
        updatedFavourites = [...currentFavourites, assetId];
      }

      favouriteAssets.set(historyId, updatedFavourites);

      return {
        ...state,
        favouriteAssets,
        error: null,
      };
    }
  );

  public readonly setLoading = this.updater((state, loading: boolean) => ({
    ...state,
    loading,
  }));

  public readonly setError = this.updater((state, error: string | null) => ({
    ...state,
    error,
  }));

  readonly incrementUpdating = this.updater((state) => ({
    ...state,
    updatingCount: state.updatingCount + 1,
  }));

  readonly decrementUpdating = this.updater((state) => ({
    ...state,
    updatingCount: Math.max(0, state.updatingCount - 1),
  }));

  public readonly fetchAnalysisStates = this.effect((params$: Observable<{ sampleId: string; caseId: string }>) => {
    return params$.pipe(
      switchMap(({ sampleId }) => {
        return from(this.analysisStateApi.fetchAnalysisStates(sampleId)).pipe(
          tap({
            next: (analysisStates) => {
              this.setAnalysisStates(analysisStates);
            },
            error: (error) => {
              this.setError(`Failed to fetch analysis states: ${error.message}`);
            },
          }),
          catchError(() => of(null))
        );
      })
    );
  });

  public readonly setReference = this.effect((sampleId$: Observable<string>) => {
    return sampleId$.pipe(
      concatLatestFrom(() => [this.currentUserAnalysisState$]),
      switchMap(([sampleId, currentUserAnalysisState]) => {
        if (!currentUserAnalysisState) {
          return EMPTY;
        }

        const updatedState: IAnalysisState = {
          ...currentUserAnalysisState,
          reference: !currentUserAnalysisState.reference,
        };

        this.updateAnalysisState(updatedState);

        return from(this.analysisStateApi.saveAnalysisState(updatedState, sampleId, null)).pipe(
          tap({
            next: (savedAnalysisState) => {
              this.updateAnalysisState(savedAnalysisState);
            },
            error: (error) => {
              this.setError(`Failed to set reference analyst: ${error.message}`);
            },
          }),
          catchError(() => of(null))
        );
      })
    );
  });

  public readonly toggleBookmark = this.effect(
    (
      data$: Observable<{
        assetId: string | null;
        sampleId: string;
      }>
    ) => {
      return data$.pipe(
        concatLatestFrom(() => [this.currentUserAnalysisState$]),
        switchMap(([{ assetId, sampleId }, currentUserAnalysisState]) => {
          if (!currentUserAnalysisState) {
            return EMPTY;
          }

          const updatedAnalysisState: IAnalysisState = {
            ...currentUserAnalysisState,
            bookmarkAssetId: assetId || '',
          };

          // Optimistic update so the bookmark reflects immediately and survives
          // reloads (setAnalysisStates re-hydrates it from the persisted state).
          this.setBookmarkForState({ historyId: currentUserAnalysisState.id, assetId });

          return from(this.analysisStateApi.saveAnalysisState(updatedAnalysisState, sampleId, null)).pipe(
            tap({
              next: (savedAnalysisState) => {
                this.updateAnalysisState(savedAnalysisState);
              },
              error: (error) => {
                this.setError(`Failed to set bookmark: ${error.message}`);
              },
            }),
            catchError(() => of(null))
          );
        })
      );
    }
  );

  public readonly setAsFavourite = this.effect((action$: Observable<{ assetId: string; sampleId: string }>) => {
    return action$.pipe(
      concatLatestFrom(() => [this.currentUserAnalysisState$, this.favouriteAssets$]),
      switchMap(([{ assetId, sampleId }, currentUserAnalysisState, favouriteAssets]) => {
        if (!currentUserAnalysisState) {
          return EMPTY;
        }

        const currentFavourites = favouriteAssets.get(currentUserAnalysisState.id) || [];
        const updatedFavouriteAssets = currentFavourites.includes(assetId)
          ? currentFavourites.filter((id) => id !== assetId)
          : [...currentFavourites, assetId];

        const updatedAnalysisState: IAnalysisState = {
          ...currentUserAnalysisState,
          favouriteAssetIds: updatedFavouriteAssets,
        };

        return from(this.analysisStateApi.saveAnalysisState(updatedAnalysisState, sampleId, null)).pipe(
          tap({
            next: (savedAnalysisState) => {
              this.updateAnalysisState(savedAnalysisState);
              this.updateFavouriteAssets({
                historyId: savedAnalysisState.id,
                favouriteAssets: savedAnalysisState.favouriteAssetIds,
              });
            },
            error: (error) => {
              this.setError(`Failed to set new favourite asset: ${error.message}`);
            },
          }),
          catchError(() => of(null))
        );
      })
    );
  });

  /**
   * Read-then-write in ONE async function on purpose: split across an effect's `switchMap`
   * these were two subscription steps, and navigating away between them tore the effect down
   * before the save was ever issued — findings landed, state stayed READ.
   */
  private async persistUserState({
    state,
    sampleId,
    caseId,
  }: {
    state: IState;
    sampleId: string | null;
    caseId: string | null;
  }): Promise<IAnalysisState | null> {
    const userId = Parse.User.current()?.id;
    if (!userId) return null;

    const existing = await this.analysisStateApi.getUserState(userId, caseId, sampleId ?? undefined);

    // No row means the analyst produced analysis through a path that never registered them.
    const analysisState: IAnalysisState = existing
      ? { ...existing, state }
      : {
          id: undefined,
          analystId: userId,
          analystEntity: 'user' as AnalyzerEntity,
          state,
          reference: false,
          bookmarkAssetId: undefined,
          favouriteAssetIds: [],
        };

    return this.analysisStateApi.saveAnalysisState(analysisState, sampleId, caseId);
  }

  public readonly updateCaseState = this.effect((data$: Observable<{ state: IState; currentCaseId: string }>) =>
    data$.pipe(
      concatMap(({ state, currentCaseId }) =>
        defer(() => {
          this.incrementUpdating();
          return from(this.persistUserState({ state, sampleId: null, caseId: currentCaseId })).pipe(
            catchError((error) => {
              this.setError(`Failed to save analysis state: ${error.message}`);
              return of(null);
            }),
            finalize(() => this.decrementUpdating())
          );
        })
      )
    )
  );

  public readonly updateSampleState = this.effect((data$: Observable<{ sampleId: string; state: IState }>) =>
    data$.pipe(
      concatMap(({ sampleId, state }) =>
        defer(() => {
          this.incrementUpdating();
          return from(this.persistUserState({ state, sampleId, caseId: null })).pipe(
            tap((savedAnalysisState) => {
              if (savedAnalysisState) this.updateAnalysisState(savedAnalysisState);
            }),
            catchError((error) => {
              this.setError(`Failed to save sample analysis state: ${error.message}`);
              return of(null);
            }),
            finalize(() => this.decrementUpdating())
          );
        })
      )
    )
  );

  private readonly _analystStateEnsured$ = new Subject<IAnalysisState>();
  public readonly analystStateEnsured$ = this._analystStateEnsured$.asObservable();

  public readonly ensureCurrentAnalystState = this.effect((params$: Observable<{ sampleId: string }>) =>
    params$.pipe(
      switchMap(({ sampleId }) => {
        const currentUserId = Parse.User.current()?.id;
        if (!currentUserId) return EMPTY;

        return from(this.analysisStateApi.getUserState(currentUserId, null, sampleId)).pipe(
          switchMap((analysisState) =>
            analysisState
              ? of(analysisState)
              : from(
                  this.analysisStateApi.saveAnalysisState(
                    {
                      id: undefined,
                      analystId: currentUserId,
                      analystEntity: 'user' as AnalyzerEntity,
                      state: IState.READ,
                      reference: false,
                      bookmarkAssetId: undefined,
                      favouriteAssetIds: [],
                    },
                    sampleId,
                    null
                  )
                )
          ),
          tap((analysisState) => {
            this.updateAnalysisState(analysisState);
            this._analystStateEnsured$.next(analysisState);
          }),
          catchError((error) => {
            this.setError(`Failed to ensure analyst state: ${error.message}`);
            return EMPTY;
          })
        );
      })
    )
  );
}
